import Dialog from "@material-ui/core/Dialog";
import moment from "moment";
import { useMemo, useState } from "react";
import {
  LuBuilding,
  LuClock,
  LuLoader,
  LuMail,
  LuSearch,
  LuVideo,
  LuX
} from "react-icons/lu";

export default function ViewAllBookingsModal({
  open,
  handleClose,
  bookings,
  setRescheduleBooking,
  setConfirmCancel,
  cancellingId,
  selectedDateStr,
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredBookings = useMemo(() => {
    let result = [...bookings];

    const now = new Date();
    result.sort((a, b) => {
      const aPast = new Date(a.end) < now;
      const bPast = new Date(b.end) < now;
      if (aPast === bPast) {
        return new Date(a.start) - new Date(b.start);
      }
      return aPast ? 1 : -1;
    });

    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(
        (b) =>
          (b.requesterName && b.requesterName.toLowerCase().includes(lowerQuery)) ||
          (b.requesterEmail && b.requesterEmail.toLowerCase().includes(lowerQuery))
      );
    }

    return result;
  }, [bookings, searchQuery]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: "!rounded-none shadow-xl border border-slate-200 overflow-hidden m-2 flex flex-col max-h-[85vh]" }}
    >
      {/* Professional Clean Header */}
      <div className="px-3.5 py-1.5 bg-[#f8fafc] border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 shrink-0">
        <h3 className="text-[12px] font-medium text-slate-900 uppercase tracking-wider">
          {selectedDateStr ? moment(selectedDateStr).format("MMMM D, YYYY") + " Bookings" : "All Demo Bookings"}
        </h3>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-56">
            <LuSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1 bg-white border border-slate-300 rounded-none text-[11px] font-medium focus:outline-none focus:border-[#313C4C] transition-all shadow-2xs"
            />
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-200/80 rounded-none transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
            title="Close"
          >
            <LuX className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 overflow-y-auto bg-slate-50/50 flex-1 scroll-thin">
        {filteredBookings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-10 h-10 bg-white rounded-none border border-slate-200 flex items-center justify-center mb-2 shadow-2xs">
              <LuSearch className="w-5 h-5 text-slate-400" />
            </div>
            <h5 className="font-medium text-[11px] text-slate-700 uppercase tracking-wider">No bookings found</h5>
            <p className="text-[11px] text-slate-500 mt-0.5 max-w-[200px] font-medium">
              {searchQuery ? "Try adjusting your search terms." : "You don't have any demo bookings yet."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {filteredBookings.map((booking) => {
              const isPast = new Date(booking.end) < new Date();

              return (
                <div
                  key={booking.eventId}
                  className={`p-2.5 bg-white border rounded-none transition-all shadow-2xs flex flex-col md:flex-row md:items-center gap-2.5
                    ${isPast ? "border-slate-200 opacity-75" : "border-slate-200 hover:border-slate-300"}
                  `}
                >
                  {/* Time Info */}
                  <div className="w-full md:w-48 shrink-0 border-b md:border-b-0 md:border-r border-slate-100 pb-2 md:pb-0 md:pr-3">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <div className={`w-1.5 h-1.5 rounded-none ${isPast ? 'bg-slate-400' : 'bg-[#313C4C]'}`} />
                      <span className={`text-[11px] font-medium ${isPast ? 'text-slate-600' : 'text-[#313C4C]'}`}>
                        {moment(booking.start).format("MMM D, YYYY")}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-medium ml-3">
                      {moment(booking.start).format("h:mm A")} to {moment(booking.end).format("h:mm A")}
                    </div>
                    {isPast && (
                      <div className="ml-3 mt-1">
                        <span className="text-[9px] font-medium uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-none border border-slate-200">
                          Completed
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Attendee Info */}
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <p className="font-medium text-slate-900 text-[11px] truncate">
                      {booking.requesterName || "Unknown Attendee"}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <div className="flex items-center gap-1 text-[10px] text-slate-500 truncate font-medium">
                        <LuMail className="w-3 h-3 shrink-0 text-slate-400" />
                        <span className="truncate">{booking.requesterEmail}</span>
                      </div>
                      {booking.requesterCompany && (
                        <div className="flex items-center gap-1 text-[10px] text-slate-500 truncate font-medium">
                          <LuBuilding className="w-3 h-3 shrink-0 text-slate-400" />
                          <span className="truncate">{booking.requesterCompany}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center justify-start md:justify-end gap-1.5 mt-1 md:mt-0 shrink-0">
                    {booking.meetLink && (
                      <a
                        href={booking.meetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-center gap-1 px-2.5 py-1 rounded-none text-[10px] font-medium uppercase tracking-wider transition-colors
                          ${isPast
                            ? "bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
                            : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-300"
                          }
                        `}
                      >
                        <LuVideo className="w-3 h-3" />
                        Join Meet
                      </a>
                    )}

                    {!isPast && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setRescheduleBooking(booking);
                          }}
                          className="flex items-center justify-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-none text-[10px] font-medium uppercase tracking-wider hover:bg-slate-200 border border-slate-200 transition-colors"
                        >
                          <LuClock className="w-3 h-3" />
                          Reschedule
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmCancel(booking);
                          }}
                          disabled={cancellingId === booking.eventId}
                          className="flex items-center justify-center gap-1 px-2 py-1 bg-rose-50 text-rose-700 rounded-none text-[10px] font-medium uppercase tracking-wider hover:bg-rose-100 border border-rose-200 transition-colors disabled:opacity-50"
                        >
                          {cancellingId === booking.eventId ? (
                            <LuLoader className="w-3 h-3 animate-spin" />
                          ) : (
                            <LuX className="w-3 h-3" />
                          )}
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
}
